/**
 * Are we inside the APK, and if so what can we do that the browser cannot (brief §15)?
 *
 * Everything here goes through `window.Capacitor` rather than through an import. That is
 * deliberate: `web/` has no Capacitor dependency, does not know the plugins exist at build
 * time, and produces one bundle that runs unchanged in a browser and inside the app. Adding
 * `@capacitor/core` to `web/package.json` would put the bridge into everybody's browser
 * download to serve the one platform that already provides it.
 *
 * The cost is that the plugin surface is typed here by hand. That is a fair trade for a
 * handful of calls, and the alternative — a build flag and two bundles — is worse.
 */

type PluginCall<T> = (options?: Record<string, unknown>) => Promise<T>;

/** What `addListener` hands back. Capacitor promises the handle, not when it arrives. */
type ListenerHandle = { remove?: () => Promise<void> } | Promise<{ remove?: () => Promise<void> }>;

type PluginSurface = Record<string, PluginCall<unknown>> & {
  addListener?: (event: string, listener: (data: unknown) => void) => ListenerHandle;
};

type CapacitorBridge = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: Record<string, PluginSurface | undefined>;
};

declare global {
  interface Window {
    Capacitor?: CapacitorBridge;
  }
}

const bridge = (): CapacitorBridge | undefined =>
  typeof window === 'undefined' ? undefined : window.Capacitor;

export function isNative(): boolean {
  return bridge()?.isNativePlatform?.() === true;
}

export function platform(): 'web' | 'android' | 'ios' {
  const name = bridge()?.getPlatform?.();
  return name === 'android' || name === 'ios' ? name : 'web';
}

/**
 * An Android phone in a browser — the one audience an APK link is useful to.
 *
 * Deliberately **not** `platform() === 'android'`: that is true inside the app, where offering
 * to install the app is nonsense. This is the other case, and the user agent is the only thing
 * that knows it.
 */
export function isAndroidBrowser(): boolean {
  if (isNative() || typeof navigator === 'undefined') return false;
  return /android/i.test(navigator.userAgent);
}

/**
 * Whether alerts keep working with the app closed.
 *
 * The honest answer, and the settings page prints it: **no on the web**. There is no
 * background geofencing API a browser will run while it is not open, and pretending otherwise
 * leaves somebody waiting for a notification that was never going to arrive.
 */
export function hasBackgroundAlerts(): boolean {
  return isNative();
}

function plugin(name: string): PluginSurface | undefined {
  return bridge()?.Plugins?.[name];
}

async function call<T>(
  pluginName: string,
  method: string,
  options?: Record<string, unknown>,
): Promise<T | undefined> {
  const target = plugin(pluginName)?.[method];
  if (target === undefined) return undefined;
  try {
    return (await target(options)) as T;
  } catch {
    // A plugin that is present but refuses is a platform saying no — a denied permission, a
    // service that will not start. The caller degrades; it never gets a dialogue.
    return undefined;
  }
}

/** Values that survive an app restart. Falls back to `localStorage` in a browser. */
export const preferences = {
  async get(key: string): Promise<string | null> {
    if (isNative()) {
      const result = await call<{ value: string | null }>('Preferences', 'get', { key });
      return result?.value ?? null;
    }
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  async set(key: string, value: string): Promise<void> {
    if (isNative()) {
      await call('Preferences', 'set', { key, value });
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch {
      // Private browsing or a full quota. Nothing here is worth failing a user action for.
    }
  },

  async remove(key: string): Promise<void> {
    if (isNative()) {
      await call('Preferences', 'remove', { key });
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // As above.
    }
  },
};

/**
 * A **local** notification — the geofence alert (brief §10, §16).
 *
 * Local on both platforms and for the same reason: no server is involved, so no position ever
 * leaves the device. On Android this also means no Firebase project, no FCM key and no Google
 * account anywhere in the build.
 */
export async function showLocalNotification(title: string, body: string): Promise<boolean> {
  if (!isNative()) return false;
  const result = await call('LocalNotifications', 'schedule', {
    notifications: [
      {
        // A stable id per minute: two alerts a minute apart are two notifications, the same
        // alert delivered twice is one.
        id: Math.floor(Date.now() / 60_000) % 2_147_483_647,
        title,
        body,
        smallIcon: 'ic_stat_hotspotter',
      },
    ],
  });
  return result !== undefined;
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!isNative()) return false;
  const result = await call<{ display: string }>('LocalNotifications', 'requestPermissions');
  return result?.display === 'granted';
}

/**
 * Ask for location, in the order Android insists on (brief §15).
 *
 * Foreground first, then background as a **second, separate** request. Android rejects the
 * combined one outright, and the rejection is indistinguishable from the user declining — so
 * the app would report "you said no" to somebody who never saw a dialogue.
 */
export async function requestLocationPermissions(): Promise<{
  foreground: boolean;
  background: boolean;
}> {
  if (!isNative()) return { foreground: true, background: false };

  const foreground = await call<{ location: string }>('Geolocation', 'requestPermissions', {
    permissions: ['location'],
  });
  const granted = foreground?.location === 'granted';
  if (!granted) return { foreground: false, background: false };

  const background = await call<{ location: string }>('Geolocation', 'requestPermissions', {
    permissions: ['coarseLocation', 'location'],
  });
  return { foreground: true, background: background?.location === 'granted' };
}

/** Open a URL in a Custom Tab — the passkey ceremony, which a WebView cannot run. */
export async function openInBrowser(url: string): Promise<boolean> {
  if (!isNative()) {
    window.open(url, '_blank', 'noopener');
    return true;
  }
  return (await call('Browser', 'open', { url })) !== undefined;
}

export async function closeBrowser(): Promise<void> {
  if (isNative()) await call('Browser', 'close');
}

/**
 * The App Link coming back into the app (brief §11.7, §15).
 *
 * Two entry points, because Android has two: `appUrlOpen` fires when the app is already
 * running, and the launch URL is what a *cold start* carries — the app having been killed
 * while the Custom Tab was in front of it is not unusual, and without the second one the
 * pairing code in that URL is simply lost.
 *
 * Returns a function that unsubscribes. It resolves the handle lazily because Capacitor may
 * hand back a promise, and a caller that unsubscribes before it settles must still work.
 */
export function onAppUrlOpen(listener: (url: string) => void): () => void {
  const add = plugin('App')?.addListener;
  if (add === undefined) return () => undefined;
  const handle = add('appUrlOpen', (data) => {
    const url = (data as { url?: unknown } | undefined)?.url;
    if (typeof url === 'string') listener(url);
  });
  let removed = false;
  void Promise.resolve(handle).then((resolved) => {
    if (removed) void resolved?.remove?.();
  });
  return () => {
    removed = true;
    void Promise.resolve(handle).then((resolved) => resolved?.remove?.());
  };
}

/**
 * Android's back gesture, taken over from the WebView.
 *
 * Capacitor's default is to walk the WebView's history and, at the end of it, do nothing —
 * which in a single-page app means the back gesture appears to be broken. Registering a
 * listener replaces that default entirely, so whoever calls this owns the whole behaviour:
 * closing what is open, and leaving.
 */
export function onBackButton(listener: () => void): () => void {
  const add = plugin('App')?.addListener;
  if (add === undefined) return () => undefined;
  const handle = add('backButton', () => listener());
  return () => {
    void Promise.resolve(handle).then((resolved) => resolved?.remove?.());
  };
}

/** Leave the app. Android only — a browser tab cannot close itself. */
export async function exitApp(): Promise<void> {
  if (isNative()) await call('App', 'exitApp');
}

/** The URL the app was launched with, if it was launched by one. */
export async function launchUrl(): Promise<string | undefined> {
  if (!isNative()) return undefined;
  const result = await call<{ url?: string }>('App', 'getLaunchUrl');
  return typeof result?.url === 'string' ? result.url : undefined;
}
